import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Lock, User, AlertCircle, X, Send, Mail, MessageSquare, Fingerprint } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { AccountRequest } from '../types';
import { sanitizeInput, validateEmail, isBiometricSupported, getBiometricName, getLastBiometricUsername, hasBiometricEnabled } from '../utils/security';
interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
  onBiometricLogin: (username: string) => Promise<boolean>;
}
export function LoginPage({
  onLogin,
  onBiometricLogin
}: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [lastUsername, setLastUsername] = useState<string | null>(null);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  useEffect(() => {
    // Check if biometric is supported and user has it enabled
    const checkBiometric = async () => {
      if (isBiometricSupported()) {
        const lastUser = getLastBiometricUsername();
        if (lastUser && hasBiometricEnabled(lastUser)) {
          setBiometricAvailable(true);
          setLastUsername(lastUser);
          setUsername(lastUser);
          setShowBiometricPrompt(true);
        }
      }
    };
    checkBiometric();
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    if (!username || !password) {
      setError('Please enter both username and password');
      setIsLoading(false);
      return;
    }
    try {
      const success = await onLogin(username, password);
      if (!success) {
        setError('Invalid username or password');
        setPassword('');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleBiometricLogin = async () => {
    if (!lastUsername) return;
    setIsLoading(true);
    setError('');
    try {
      const success = await onBiometricLogin(lastUsername);
      if (!success) {
        setError('Biometric authentication failed. Please use your password.');
        setShowBiometricPrompt(false);
      }
    } catch (err) {
      setError('Biometric authentication is not available. Please use your password.');
      setShowBiometricPrompt(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleRequestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !email) {
      return;
    }
    // Validate email
    if (!validateEmail(email)) {
      return;
    }
    // Sanitize inputs
    const sanitizedName = sanitizeInput(fullName.trim());
    const sanitizedEmail = sanitizeInput(email.trim());
    const sanitizedMessage = message ? sanitizeInput(message.trim()) : undefined;
    // Get existing requests
    const existingRequests = JSON.parse(localStorage.getItem('account_requests') || '[]') as AccountRequest[];
    // Check for duplicate email
    if (existingRequests.some(r => r.email === sanitizedEmail && r.status === 'pending')) {
      alert('A request with this email already exists.');
      return;
    }
    // Create new request
    const newRequest: AccountRequest = {
      id: uuidv4(),
      fullName: sanitizedName,
      email: sanitizedEmail,
      message: sanitizedMessage,
      timestamp: new Date().toISOString(),
      status: 'pending'
    };
    // Save request
    existingRequests.push(newRequest);
    localStorage.setItem('account_requests', JSON.stringify(existingRequests));
    // Show success and reset form
    setRequestSuccess(true);
    setFullName('');
    setEmail('');
    setMessage('');
    setTimeout(() => {
      setShowRequestModal(false);
      setRequestSuccess(false);
    }, 2000);
  };
  return <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4" data-id="element-397">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="w-full max-w-md" data-id="element-398">
        <div className="text-center mb-8" data-id="element-399">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl mb-4 shadow-lg shadow-amber-900/20" data-id="element-400">
            <Wallet size={40} className="text-white" data-id="element-401" />
          </div>
          <h1 className="text-3xl font-bold text-stone-50 mb-2" data-id="element-402">
            Smart Budget
          </h1>
          <p className="text-stone-400" data-id="element-403">Sign in to manage your finances</p>
        </div>

        <div className="bg-stone-900 rounded-3xl p-8 border border-stone-800 shadow-2xl" data-id="element-404">
          {/* Biometric Login Option */}
          {biometricAvailable && showBiometricPrompt && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="mb-6" data-id="element-405">
              <button onClick={handleBiometricLogin} disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-2xl hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg shadow-emerald-900/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3" data-id="element-406">
                <Fingerprint size={24} data-id="element-407" />
                <div className="text-left" data-id="element-408">
                  <div className="text-sm font-bold" data-id="element-409">
                    Sign in with {getBiometricName()}
                  </div>
                  <div className="text-xs opacity-80" data-id="element-410">{lastUsername}</div>
                </div>
              </button>

              <div className="relative my-6" data-id="element-411">
                <div className="absolute inset-0 flex items-center" data-id="element-412">
                  <div className="w-full border-t border-stone-800" data-id="element-413"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase" data-id="element-414">
                  <span className="bg-stone-900 px-2 text-stone-500" data-id="element-415">
                    Or use password
                  </span>
                </div>
              </div>
            </motion.div>}

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-416">
            <div data-id="element-417">
              <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-418">
                Username
              </label>
              <div className="relative" data-id="element-419">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={20} data-id="element-420" />
                <input type="text" value={username} onChange={e => setUsername(e.target.value)} disabled={isLoading} className="w-full bg-stone-800 text-stone-50 py-4 pl-12 pr-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all disabled:opacity-50" placeholder="Enter your username" autoComplete="username" data-id="element-421" />
              </div>
            </div>

            <div data-id="element-422">
              <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-423">
                Password
              </label>
              <div className="relative" data-id="element-424">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={20} data-id="element-425" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={isLoading} className="w-full bg-stone-800 text-stone-50 py-4 pl-12 pr-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all disabled:opacity-50" placeholder="Enter your password" autoComplete="current-password" data-id="element-426" />
              </div>
            </div>

            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-id="element-427">
                <AlertCircle size={16} data-id="element-428" />
                {error}
              </motion.div>}

            <button type="submit" disabled={isLoading} className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-2xl hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg shadow-amber-900/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-429">
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        <button onClick={() => setShowRequestModal(true)} className="w-full text-center text-stone-400 hover:text-amber-500 text-sm mt-6 transition-colors" data-id="element-430">
          Don't have an account?{' '}
          <span className="font-semibold" data-id="element-431">Request Access</span>
        </button>
      </motion.div>

      {/* Request Account Modal */}
      <AnimatePresence data-id="element-432">
        {showRequestModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !requestSuccess && setShowRequestModal(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-433" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-434">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.9,
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} className="bg-stone-900 rounded-3xl p-6 w-full max-w-md border border-stone-800 shadow-2xl pointer-events-auto max-h-[90vh] overflow-y-auto" data-id="element-435">
                {!requestSuccess ? <>
                    <div className="flex justify-between items-center mb-6" data-id="element-436">
                      <h2 className="text-2xl font-bold text-stone-50" data-id="element-437">
                        Request Account
                      </h2>
                      <button onClick={() => setShowRequestModal(false)} className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-stone-200" data-id="element-438">
                        <X size={20} data-id="element-439" />
                      </button>
                    </div>

                    <form onSubmit={handleRequestSubmit} className="space-y-4" data-id="element-440">
                      <div data-id="element-441">
                        <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-442">
                          Full Name *
                        </label>
                        <div className="relative" data-id="element-443">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={18} data-id="element-444" />
                          <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-3 pl-11 pr-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Enter your full name" maxLength={100} data-id="element-445" />
                        </div>
                      </div>

                      <div data-id="element-446">
                        <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-447">
                          Email Address *
                        </label>
                        <div className="relative" data-id="element-448">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={18} data-id="element-449" />
                          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-3 pl-11 pr-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="your.email@example.com" maxLength={100} data-id="element-450" />
                        </div>
                      </div>

                      <div data-id="element-451">
                        <label className="block text-xs font-medium text-stone-500 uppercase mb-2" data-id="element-452">
                          Message (Optional)
                        </label>
                        <div className="relative" data-id="element-453">
                          <MessageSquare className="absolute left-4 top-4 text-stone-500" size={18} data-id="element-454" />
                          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={500} className="w-full bg-stone-800 text-stone-50 py-3 pl-11 pr-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none" placeholder="Why do you want to create an account?" data-id="element-455" />
                        </div>
                      </div>

                      <button type="submit" className="w-full py-3 bg-amber-500 text-stone-900 font-bold rounded-xl hover:bg-amber-400 transition-colors flex items-center justify-center gap-2" data-id="element-456">
                        <Send size={18} data-id="element-457" />
                        Send Request
                      </button>
                    </form>
                  </> : <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="text-center py-8" data-id="element-458">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-459">
                      <Send size={32} className="text-emerald-500" data-id="element-460" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-50 mb-2" data-id="element-461">
                      Request Sent!
                    </h3>
                    <p className="text-stone-400 text-sm" data-id="element-462">
                      The admin will review your request and get back to you
                      soon.
                    </p>
                  </motion.div>}
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}